/**
 * account-status.ts — Nhãn trạng thái tài khoản kênh + các phép kiểm thuần
 * (không phụ thuộc React) dùng chung cho trang Kết nối, bộ chọn tài khoản và
 * dải cảnh báo trên ô soạn tin.
 *
 * Trạng thái sống (`liveStatus` của GET /zalo-accounts):
 *   connected     — Đã kết nối
 *   connecting    — WebSocket vừa rớt, đang tự kết nối lại (hoặc đang đăng nhập).
 *                   Tin nhận về lúc này có thể bị trễ; tin gửi có thể chưa tới khách.
 *   disconnected / kicked / token_expired — hỏng, cần người xử lý.
 */
import type { ChannelStatus } from '@/hooks/use-integrations'
import { i18n } from '@/lib/i18n'

export interface StatusMeta {
  label: string
  variant: 'default' | 'secondary' | 'success' | 'warning' | 'destructive' | 'outline'
}

/**
 * Ánh xạ trạng thái kênh → nhãn tiếng Việt + variant Badge.
 * "Đã kết nối" (xanh) và "Đang kết nối lại…" (vàng) cố ý khác chữ rõ ràng —
 * trước đây hai trạng thái dễ đọc nhầm thành một.
 */
export function statusMeta(status: ChannelStatus | null | undefined, disabled = false): StatusMeta {
  if (disabled) return { label: i18n.t('sharedMisc:accountStatus.disabled'), variant: 'secondary' }
  switch (status) {
    case 'connected':
      return { label: i18n.t('sharedMisc:accountStatus.connected'), variant: 'success' }
    case 'connecting':
      return { label: i18n.t('sharedMisc:accountStatus.connecting'), variant: 'warning' }
    case 'qr_pending':
      return { label: i18n.t('sharedMisc:accountStatus.qrPending'), variant: 'warning' }
    case 'webhook_pending':
      return { label: i18n.t('sharedMisc:accountStatus.webhookPending'), variant: 'warning' }
    case 'token_expired':
      return { label: i18n.t('sharedMisc:accountStatus.tokenExpired'), variant: 'destructive' }
    case 'kicked':
      return { label: i18n.t('sharedMisc:accountStatus.kicked'), variant: 'destructive' }
    case 'disconnected':
      return { label: i18n.t('sharedMisc:accountStatus.disconnected'), variant: 'destructive' }
    case 'read_only':
      // Telegram Business: chủ tài khoản chưa cấp quyền trả lời cho bot.
      return { label: i18n.t('sharedMisc:accountStatus.readOnly'), variant: 'warning' }
    default:
      return { label: i18n.t('sharedMisc:accountStatus.notConnected'), variant: 'secondary' }
  }
}

/**
 * Kênh ảo "🧪 AI Sandbox" của chat thử nhân viên AI (khớp backend
 * `shared/sandbox-channel.ts`): external_uid = 'ai-sandbox-<orgId>'. Không bao giờ
 * kết nối thật — không được báo "mất kết nối" cho nó.
 */
export const SANDBOX_UID_PREFIX = 'ai-sandbox-'

export function isSandboxAccount(acc: { externalUid?: string | null } | null | undefined): boolean {
  return !!acc?.externalUid && acc.externalUid.startsWith(SANDBOX_UID_PREFIX)
}

export interface AccountStatusTarget {
  liveStatus?: ChannelStatus | null
  isDisabled?: boolean
  externalUid?: string | null
}

/**
 * Tài khoản đang KHÔNG chắc gửi/nhận được: hỏng hẳn (disconnected / kicked /
 * token_expired) hoặc đang kết nối lại (connecting). Không tính qr_pending (đang
 * thêm tài khoản), tài khoản cố ý tắt, và kênh ảo AI Sandbox.
 */
export function isAccountDown(acc: AccountStatusTarget | null | undefined): boolean {
  if (!acc || acc.isDisabled || isSandboxAccount(acc)) return false
  return (
    acc.liveStatus === 'disconnected' ||
    acc.liveStatus === 'kicked' ||
    acc.liveStatus === 'token_expired' ||
    acc.liveStatus === 'connecting'
  )
}

/** Đang tự kết nối lại — cảnh báo nhẹ (vàng) thay vì đỏ. */
export function isAccountReconnecting(acc: AccountStatusTarget | null | undefined): boolean {
  return isAccountDown(acc) && acc?.liveStatus === 'connecting'
}

/** Câu cảnh báo cho dải trên ô soạn tin. */
export function accountDownMessage(acc: AccountStatusTarget & { displayName?: string | null }): { title: string; detail: string } {
  const named = !!acc.displayName
  const name = acc.displayName ?? ''
  switch (acc.liveStatus) {
    case 'connecting':
      return {
        title: i18n.t(named ? 'sharedMisc:accountStatus.downMessage.connecting.titleNamed' : 'sharedMisc:accountStatus.downMessage.connecting.titleGeneric', { name }),
        detail: i18n.t('sharedMisc:accountStatus.downMessage.connecting.detail'),
      }
    case 'kicked':
      return {
        title: i18n.t(named ? 'sharedMisc:accountStatus.downMessage.kicked.titleNamed' : 'sharedMisc:accountStatus.downMessage.kicked.titleGeneric', { name }),
        detail: i18n.t('sharedMisc:accountStatus.downMessage.needReconnectDetail'),
      }
    case 'token_expired':
      return {
        title: i18n.t(named ? 'sharedMisc:accountStatus.downMessage.tokenExpired.titleNamed' : 'sharedMisc:accountStatus.downMessage.tokenExpired.titleGeneric', { name }),
        detail: i18n.t('sharedMisc:accountStatus.downMessage.needReconnectDetail'),
      }
    default:
      return {
        title: i18n.t(named ? 'sharedMisc:accountStatus.downMessage.disconnected.titleNamed' : 'sharedMisc:accountStatus.downMessage.disconnected.titleGeneric', { name }),
        detail: i18n.t('sharedMisc:accountStatus.downMessage.needReconnectDetail'),
      }
  }
}
